import { useState, useEffect } from "react";
import { LoaderCircle } from "lucide-react";
import { useBoard } from "@/components/board/useBoard";
import { FilterSidebar } from "@/components/board/FilterSidebar";
import { TaskBoard } from "@/components/board/TaskBoard";
import { BoardHeader } from "@/components/board/BoardHeader";
import { TaskDialog } from "@/components/board/TaskDialog";
import { CreateSprintDialog } from "@/components/board/CreateSprintDialog";
import { Task } from "@/components/board/boardTypes";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/useTranslation";
import { fetchAllSprints } from "@/components/board/boardService";

const Board = () => {
  const { t } = useTranslation();
  const {
    boardData,
    setBoardData,
    selectedRole,
    setSelectedRole,
    selectedSprintId,
    setSelectedSprintId,
    selectedBoardId,
    setSelectedBoardId,
    sidebarExpanded,
    setSidebarExpanded,
    loading,
    boards,
    selectedProject,
    selectedSprint,
    openBoardSelector,
    setOpenBoardSelector,
    handleCreateTask,
    handleUpdateTask,
    handleCreateSprint,
    fetchTasks
  } = useBoard();

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogMode, setTaskDialogMode] = useState<'create' | 'edit'>('create');
  const [createSprintDialogOpen, setCreateSprintDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  // Add a key state to force FilterSidebar re-render when sprints change
  const [sprintsKey, setSprintsKey] = useState(0);

  // Subscribe to sprint changes at the Board level
  useEffect(() => {
    if (!selectedBoardId) return;

    console.log(`Board.tsx: Setting up sprints subscription for board ${selectedBoardId}`);
    
    const channelName = `board-sprint-changes-${selectedBoardId}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'sprints',
        filter: `board_id=eq.${selectedBoardId}`
      }, (payload) => {
        console.log('Board.tsx: Sprint created:', payload);
        // Increment the key to force a re-render
        setSprintsKey(prev => prev + 1);
      })
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'sprints',
        filter: `board_id=eq.${selectedBoardId}`
      }, () => {
        console.log('Board.tsx: Sprint updated');
        setSprintsKey(prev => prev + 1);
      })
      .on('postgres_changes', { 
        event: 'DELETE', 
        schema: 'public', 
        table: 'sprints',
        filter: `board_id=eq.${selectedBoardId}`
      }, () => {
        console.log('Board.tsx: Sprint deleted');
        setSprintsKey(prev => prev + 1);
      })
      .subscribe((status) => {
        console.log(`Board.tsx: Subscription status for ${channelName}:`, status);
      });

    return () => {
      console.log(`Board.tsx: Removing channel ${channelName}`);
      supabase.removeChannel(channel);
    };
  }, [selectedBoardId]);

  // Helper method to get all sprints for a board
  const getAllSprints = async () => {
    if (!selectedBoardId) return [];
    return await fetchAllSprints(selectedBoardId);
  };

  // Force a refresh when a sprint is created through the dialog
  const handleSprintCreation = async (title: string, startDate: Date, endDate: Date, status: string, boardId: string | null) => {
    await handleCreateSprint(title, startDate, endDate, status, boardId);
    // Make sure to increment the key even if the subscription misses the change
    setSprintsKey(prev => prev + 1);
    console.log('Board.tsx: handleSprintCreation - incrementing sprintsKey');
  };

  // Handle task creation
  const handleOpenCreateTask = () => {
    setTaskDialogMode('create');
    setSelectedTask(null);
    setTaskDialogOpen(true);
  };

  // Handle task edit
  const handleTaskEdit = (task: Task) => {
    setTaskDialogMode('edit');
    setSelectedTask(task);
    setTaskDialogOpen(true);
  };

  // Handle task submission (creates or updates depending on mode)
  const handleTaskSubmit = (title: string, description: string, role: any, sprintId: string, employeeId?: number) => {
    if (taskDialogMode === 'create') {
      handleCreateTask(title, description, role, sprintId, employeeId);
    } else {
      if (selectedTask) {
        handleUpdateTask(selectedTask.id, title, description, role, sprintId, employeeId);
      }
    }
  };

  // Handle task deletion
  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);
        
      if (error) throw error;
      
      // Update the board data by removing the deleted task
      const updatedTasks = { ...boardData.tasks };
      delete updatedTasks[taskId];
      
      // Remove task from all columns
      const updatedColumns = { ...boardData.columns };
      Object.keys(updatedColumns).forEach(columnId => {
        updatedColumns[columnId] = {
          ...updatedColumns[columnId],
          taskIds: updatedColumns[columnId].taskIds.filter(id => id !== taskId)
        };
      });
      
      setBoardData({
        tasks: updatedTasks,
        columns: updatedColumns,
        columnOrder: boardData.columnOrder
      });
      
      toast.success(t('board.taskDeletedSuccess'));
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(t('board.taskDeleteError'));
    }
  };

  // Handle sprint deletion with options
  const handleDeleteSprint = async (
    sprintId: string, 
    options: { deleteTasks: boolean, targetSprintId?: string }
  ) => {
    try {
      // Import deleteSprint function from boardService
      const { deleteSprint } = await import('@/components/board/boardService');
      
      await deleteSprint(sprintId, options);
      
      // If the deleted sprint was the selected one, set selectedSprintId back to 'all'
      if (selectedSprintId === sprintId) {
        setSelectedSprintId('all');
      }
      
      if (options.deleteTasks) {
        toast.success(t('board.sprintAndTasksDeletedSuccess'));
      } else {
        toast.success(t('board.sprintDeletedTasksMovedSuccess'));
      }
      
      // Force refresh of sprints list and tasks
      setSprintsKey(prev => prev + 1);
      fetchTasks();
    } catch (error) {
      console.error("Error deleting sprint:", error);
      toast.error(t('board.sprintDeleteError'));
    }
  };

  // Close task dialog
  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false);
    setSelectedTask(null);
  };

  return (
    <div className="flex h-full">
      <FilterSidebar 
        key={`sidebar-${sprintsKey}`} // Add key prop to force re-render
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        selectedSprintId={selectedSprintId}
        setSelectedSprintId={setSelectedSprintId}
        sidebarExpanded={sidebarExpanded}
        setSidebarExpanded={setSidebarExpanded}
        boardId={selectedBoardId}
        onCreateSprint={() => setCreateSprintDialogOpen(true)}
      />
      
      <div className="flex-1 p-6 overflow-hidden flex flex-col">
        <BoardHeader 
          boards={boards}
          selectedBoardId={selectedBoardId}
          setSelectedBoardId={setSelectedBoardId}
          selectedProject={selectedProject}
          selectedSprint={selectedSprint}
          openBoardSelector={openBoardSelector}
          setOpenBoardSelector={setOpenBoardSelector}
          onCreateTask={handleOpenCreateTask}
          onCreateSprint={() => setCreateSprintDialogOpen(true)}
          onDeleteSprint={handleDeleteSprint}
          getAllSprints={getAllSprints}
        />
        
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <TaskBoard 
            boardData={boardData}
            setBoardData={setBoardData}
            selectedRole={selectedRole}
            loading={loading}
            onEditTask={handleTaskEdit}
          />
        )}
      </div>
      
      <TaskDialog
        isOpen={taskDialogOpen}
        onClose={handleCloseTaskDialog}
        onSubmit={handleTaskSubmit}
        onDelete={handleDeleteTask}
        task={selectedTask}
        boardId={selectedBoardId}
        selectedSprintId={selectedSprintId}
        selectedRole={selectedRole}
        mode={taskDialogMode}
      />

      <CreateSprintDialog 
        isOpen={createSprintDialogOpen}
        onClose={() => setCreateSprintDialogOpen(false)}
        onCreateSprint={handleSprintCreation}
        boardId={selectedBoardId}
      />
    </div>
  );
};

export default Board;
